import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ShoppingCartIcon, 
  MagnifyingGlassIcon,
  Bars3Icon,
  XMarkIcon,
  UserIcon,
  ChevronDownIcon,
  PhoneIcon,
  TruckIcon
} from '@heroicons/react/24/outline';
import { useCartStore } from '../store/useCartStore';

export const Header: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [bijouxMenuOpen, setBijouxMenuOpen] = useState(false);
  const [maisonMenuOpen, setMaisonMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { setCartOpen, getItemCount } = useCartStore();
  const itemCount = getItemCount();

  // Détecter le scroll pour ajuster la position des mega menus
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/search?q=${encodeURIComponent(searchQuery)}`;
      setSearchOpen(false);
    }
  };

  return (
    <>
      {/* Barre supérieure - NON STICKY */}
      <div className="bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="w-full">
          <div className="grid grid-cols-3 items-center h-12 px-4">
            {/* Téléphone - gauche */}
            <div className="flex items-center justify-start">
              <a href="tel:+33677267276" className="flex items-center gap-2 text-gray-600 hover:text-[#497aab] transition-colors">
                <PhoneIcon className="h-4 w-4" />
                <span className="hidden sm:inline font-medium text-base">+33 6 77 26 72 76</span>
              </a>
            </div>
            
            {/* Messages défilants - centre */}
            <div className="flex items-center justify-center overflow-hidden h-12 relative">
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="animate-slideVertical">
                  <div className="h-12 flex items-center justify-center gap-2 text-gray-600">
                    <TruckIcon className="h-4 w-4" />
                    <span className="font-medium text-base whitespace-nowrap">Livraison et retour offerts</span>
                  </div>
                </div>
              </div>
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="animate-slideVertical animation-delay-4s">
                  <div className="h-12 flex items-center justify-center text-gray-600">
                    <span className="font-medium text-base whitespace-nowrap">Paiement en 3 fois sans frais</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* English - droite */}
            <div className="flex items-center justify-end">
              <button className="text-gray-600 hover:text-[#497aab] font-medium transition-colors text-base">
                English
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Header principal - STICKY */}
      <header className="sticky top-0 z-50 bg-[#497aab] shadow-lg">
        <nav className="container mx-auto px-4" aria-label="Top">
        <div className="flex items-center justify-between h-24">
          {/* Bouton menu mobile */}
            <button
              type="button"
            className="lg:hidden p-2 rounded-md text-white hover:bg-white/10 transition-colors"
              onClick={() => setMobileMenuOpen(true)}
            >
              <span className="sr-only">Ouvrir le menu</span>
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>

          {/* Logo centré */}
          <Link to="/" className="absolute left-1/2 transform -translate-x-1/2 flex items-center justify-center">
            <img 
              src="/assets/logo/aupiho-logo.png" 
              alt="Aupiho - L'or durable" 
              className="h-24 w-auto object-contain transition-transform duration-300 hover:scale-105"
            />
          </Link>

          {/* Navigation desktop - gauche */}
          <div className="hidden lg:flex items-center space-x-8 flex-1">
            <Link 
              to="/nouveautes-bijoux-aupiho-joaillerie" 
              className="text-white hover:text-white/80 font-light text-base uppercase tracking-wider transition-all duration-300 hover:translate-y-[-2px]"
            >
              Nouveautés
            </Link>
            
            {/* Mega Menu NOS BIJOUX */}
            <div 
              className="relative group"
              onMouseEnter={() => setBijouxMenuOpen(true)}
              onMouseLeave={() => setBijouxMenuOpen(false)}
            >
              <button className="flex items-center gap-1 text-white hover:text-white/80 font-light text-base uppercase tracking-wider transition-all duration-300 hover:translate-y-[-2px]">
                Nos Bijoux
                <ChevronDownIcon className={`h-4 w-4 transition-transform duration-300 ${bijouxMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Mega Menu Dropdown */}
              <div className={`fixed left-0 right-0 ${isScrolled ? 'top-[96px]' : 'top-[144px]'} bg-white shadow-2xl border-t-2 border-[#c69c70] transition-all duration-300 ${bijouxMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
                <div className="container mx-auto px-8 py-12">
                  <div className="grid grid-cols-5 gap-12">
                    
                    {/* Colonne 1 - CATÉGORIE */}
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-[#497aab] font-bold text-xs uppercase tracking-widest mb-5 pb-2 border-b-2 border-[#c69c70]">
                          Catégorie
                        </h3>
                        <div className="space-y-3">
                          <Link to="/category/bagues" className="group/item flex items-center text-gray-700 hover:text-[#c69c70] transition-all duration-200" onClick={() => setBijouxMenuOpen(false)}>
                            <span className="w-0 group-hover/item:w-2 h-0.5 bg-[#c69c70] transition-all duration-200 mr-0 group-hover/item:mr-2"></span>
                            <span className="text-sm">Bagues</span>
                          </Link>
                          <Link to="/category/boucles-doreilles" className="group/item flex items-center text-gray-700 hover:text-[#c69c70] transition-all duration-200" onClick={() => setBijouxMenuOpen(false)}>
                            <span className="w-0 group-hover/item:w-2 h-0.5 bg-[#c69c70] transition-all duration-200 mr-0 group-hover/item:mr-2"></span>
                            <span className="text-sm">Boucles d'oreilles</span>
                          </Link>
                          <Link to="/category/bracelets" className="group/item flex items-center text-gray-700 hover:text-[#c69c70] transition-all duration-200" onClick={() => setBijouxMenuOpen(false)}>
                            <span className="w-0 group-hover/item:w-2 h-0.5 bg-[#c69c70] transition-all duration-200 mr-0 group-hover/item:mr-2"></span>
                            <span className="text-sm">Bracelets</span>
                          </Link>
                          <Link to="/category/colliers" className="group/item flex items-center text-gray-700 hover:text-[#c69c70] transition-all duration-200" onClick={() => setBijouxMenuOpen(false)}>
                            <span className="w-0 group-hover/item:w-2 h-0.5 bg-[#c69c70] transition-all duration-200 mr-0 group-hover/item:mr-2"></span>
                            <span className="text-sm">Colliers</span>
                          </Link>
                          <Link to="/category/pendentifs" className="group/item flex items-center text-gray-700 hover:text-[#c69c70] transition-all duration-200" onClick={() => setBijouxMenuOpen(false)}>
                            <span className="w-0 group-hover/item:w-2 h-0.5 bg-[#c69c70] transition-all duration-200 mr-0 group-hover/item:mr-2"></span>
                            <span className="text-sm">Pendentifs</span>
                          </Link>
                          <Link to="/category/mariage" className="group/item flex items-center text-gray-700 hover:text-[#c69c70] transition-all duration-200" onClick={() => setBijouxMenuOpen(false)}>
                            <span className="w-0 group-hover/item:w-2 h-0.5 bg-[#c69c70] transition-all duration-200 mr-0 group-hover/item:mr-2"></span>
                            <span className="text-sm">Mariage</span>
                          </Link>
                        </div>
                      </div>
                    </div>

                    {/* Colonne 2 & 3 - COLLECTIONS */}
                    <div className="col-span-2 space-y-6">
                      <div>
                        <h3 className="text-[#497aab] font-bold text-xs uppercase tracking-widest mb-5 pb-2 border-b-2 border-[#c69c70]">
                          Collections
                        </h3>
                        <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                          <Link to="/category/arc-en-ciel" className="group/item flex items-center text-gray-700 hover:text-[#c69c70] transition-all duration-200" onClick={() => setBijouxMenuOpen(false)}>
                            <span className="w-0 group-hover/item:w-2 h-0.5 bg-[#c69c70] transition-all duration-200 mr-0 group-hover/item:mr-2"></span>
                            <span className="text-sm">Arc-en-ciel</span>
                          </Link>
                          <Link to="/category/origine" className="group/item flex items-center text-gray-700 hover:text-[#c69c70] transition-all duration-200" onClick={() => setBijouxMenuOpen(false)}>
                            <span className="w-0 group-hover/item:w-2 h-0.5 bg-[#c69c70] transition-all duration-200 mr-0 group-hover/item:mr-2"></span>
                            <span className="text-sm">Origine</span>
                          </Link>
                          <Link to="/category/atout-coeur" className="group/item flex items-center text-gray-700 hover:text-[#c69c70] transition-all duration-200" onClick={() => setBijouxMenuOpen(false)}>
                            <span className="w-0 group-hover/item:w-2 h-0.5 bg-[#c69c70] transition-all duration-200 mr-0 group-hover/item:mr-2"></span>
                            <span className="text-sm">Atout Coeur</span>
                          </Link>
                          <Link to="/category/sesame" className="group/item flex items-center text-gray-700 hover:text-[#c69c70] transition-all duration-200" onClick={() => setBijouxMenuOpen(false)}>
                            <span className="w-0 group-hover/item:w-2 h-0.5 bg-[#c69c70] transition-all duration-200 mr-0 group-hover/item:mr-2"></span>
                            <span className="text-sm">Sésame</span>
                          </Link>
                          <Link to="/category/celeste" className="group/item flex items-center text-gray-700 hover:text-[#c69c70] transition-all duration-200" onClick={() => setBijouxMenuOpen(false)}>
                            <span className="w-0 group-hover/item:w-2 h-0.5 bg-[#c69c70] transition-all duration-200 mr-0 group-hover/item:mr-2"></span>
                            <span className="text-sm">Céleste</span>
                          </Link>
                          <Link to="/category/tm" className="group/item flex items-center text-gray-700 hover:text-[#c69c70] transition-all duration-200" onClick={() => setBijouxMenuOpen(false)}>
                            <span className="w-0 group-hover/item:w-2 h-0.5 bg-[#c69c70] transition-all duration-200 mr-0 group-hover/item:mr-2"></span>
                            <span className="text-sm">T'M</span>
                          </Link>
                          <Link to="/category/croix" className="group/item flex items-center text-gray-700 hover:text-[#c69c70] transition-all duration-200" onClick={() => setBijouxMenuOpen(false)}>
                            <span className="w-0 group-hover/item:w-2 h-0.5 bg-[#c69c70] transition-all duration-200 mr-0 group-hover/item:mr-2"></span>
                            <span className="text-sm">Croix</span>
                          </Link>
                        </div>
                      </div>
                    </div>

                    {/* Colonne 4 & 5 - Sections spéciales */}
                    <div className="col-span-2 grid grid-cols-2 gap-6">
                      {/* Nos Bijoux Homme */}
                      <Link to="/category/homme" className="group/card relative overflow-hidden rounded-xl bg-gradient-to-br from-[#497aab] to-[#5a8bc4] p-8 hover:shadow-2xl transition-all duration-300 hover:scale-105" onClick={() => setBijouxMenuOpen(false)}>
                        <div className="relative z-10">
                          <h4 className="text-white font-bold text-lg uppercase tracking-wider mb-1">
                            Nos Bijoux
                          </h4>
                          <h4 className="text-white font-bold text-lg uppercase tracking-wider mb-4">
                            Homme
                          </h4>
                          <div className="text-white/90 text-sm font-medium flex items-center gap-2">
                            <span>Découvrir</span>
                            <span className="inline-block transform group-hover/card:translate-x-1 transition-transform">→</span>
                          </div>
                        </div>
                        <div className="absolute inset-0 bg-white/0 group-hover/card:bg-white/10 transition-colors duration-300"></div>
                      </Link>

                      {/* Carte Cadeau */}
                      <Link to="/carte-cadeau" className="group/card relative overflow-hidden rounded-xl bg-gradient-to-br from-[#c69c70] to-[#b88a5f] p-8 hover:shadow-2xl transition-all duration-300 hover:scale-105" onClick={() => setBijouxMenuOpen(false)}>
                        <div className="relative z-10">
                          <h4 className="text-white font-bold text-lg uppercase tracking-wider mb-1">
                            Carte
                          </h4>
                          <h4 className="text-white font-bold text-lg uppercase tracking-wider mb-4">
                            Cadeau
                          </h4>
                          <div className="text-white/90 text-sm font-medium flex items-center gap-2">
                            <span>Offrir</span>
                            <span className="inline-block transform group-hover/card:translate-x-1 transition-transform">→</span>
                          </div>
                        </div>
                        <div className="absolute inset-0 bg-white/0 group-hover/card:bg-white/10 transition-colors duration-300"></div>
            </Link>
          </div>

                  </div>
                </div>
              </div>
            </div>

            {/* Mega Menu MAISON AUPIHO */}
            <div 
              className="relative group"
              onMouseEnter={() => setMaisonMenuOpen(true)}
              onMouseLeave={() => setMaisonMenuOpen(false)}
            >
              <button className="flex items-center gap-1 text-white hover:text-white/80 font-light text-base uppercase tracking-wider transition-all duration-300 hover:translate-y-[-2px]">
                Maison Aupiho
                <ChevronDownIcon className={`h-4 w-4 transition-transform duration-300 ${maisonMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Mega Menu Dropdown */}
              <div className={`fixed left-0 right-0 ${isScrolled ? 'top-[96px]' : 'top-[144px]'} bg-white shadow-2xl border-t-2 border-[#c69c70] transition-all duration-300 ${maisonMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
                <div className="container mx-auto px-8 py-12">
                  <div className="max-w-2xl mx-auto">
                    <h3 className="text-[#497aab] font-bold text-xs uppercase tracking-widest mb-8 pb-2 border-b-2 border-[#c69c70] text-center">
                      Maison Aupiho
                    </h3>
                    <div className="grid grid-cols-2 gap-x-16 gap-y-4">
                      <Link to="/notre-histoire" className="group/item flex items-center text-gray-700 hover:text-[#c69c70] transition-all duration-200" onClick={() => setMaisonMenuOpen(false)}>
                        <span className="w-0 group-hover/item:w-2 h-0.5 bg-[#c69c70] transition-all duration-200 mr-0 group-hover/item:mr-2"></span>
                        <span className="text-sm">Notre histoire</span>
                      </Link>
                      <Link to="/nos-engagements-aupiho-joaillerie" className="group/item flex items-center text-gray-700 hover:text-[#c69c70] transition-all duration-200" onClick={() => setMaisonMenuOpen(false)}>
                        <span className="w-0 group-hover/item:w-2 h-0.5 bg-[#c69c70] transition-all duration-200 mr-0 group-hover/item:mr-2"></span>
                        <span className="text-sm">Nos engagements</span>
                      </Link>
                      <Link to="/or-recycle" className="group/item flex items-center text-gray-700 hover:text-[#c69c70] transition-all duration-200" onClick={() => setMaisonMenuOpen(false)}>
                        <span className="w-0 group-hover/item:w-2 h-0.5 bg-[#c69c70] transition-all duration-200 mr-0 group-hover/item:mr-2"></span>
                        <span className="text-sm">Or recyclé</span>
                      </Link>
                      <Link to="/journal" className="group/item flex items-center text-gray-700 hover:text-[#c69c70] transition-all duration-200" onClick={() => setMaisonMenuOpen(false)}>
                        <span className="w-0 group-hover/item:w-2 h-0.5 bg-[#c69c70] transition-all duration-200 mr-0 group-hover/item:mr-2"></span>
                        <span className="text-sm">Journal</span>
            </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation desktop - droite */}
          <div className="hidden lg:flex items-center space-x-8 flex-1 justify-end">
            <Link 
              to="/bijoux-sur-mesure" 
              className="text-white hover:text-white/80 font-light text-base uppercase tracking-wider transition-all duration-300 hover:translate-y-[-2px]"
            >
              Bijoux sur mesure
            </Link>
            <Link 
              to="/points-de-vente" 
              className="text-white hover:text-white/80 font-light text-base uppercase tracking-wider transition-all duration-300 hover:translate-y-[-2px]"
            >
              Points de vente
            </Link>
            <Link 
              to="/contactez-nous" 
              className="text-white hover:text-white/80 font-light text-base uppercase tracking-wider transition-all duration-300 hover:translate-y-[-2px]"
            >
              Contact
            </Link>
          </div>

          {/* Actions - droite */}
          <div className="flex items-center gap-3 ml-auto lg:ml-0">
            {/* Recherche */}
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <span className="sr-only">Rechercher</span>
              <MagnifyingGlassIcon className="h-5 w-5" />
            </button>

            {/* Compte utilisateur */}
            <button className="hidden md:block p-2 text-white hover:bg-white/10 rounded-lg transition-colors">
              <span className="sr-only">Mon compte</span>
              <UserIcon className="h-5 w-5" />
            </button>

            {/* Panier */}
            <button
              onClick={() => setCartOpen(true)}
              className="relative p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <span className="sr-only">Panier</span>
              <ShoppingCartIcon className="h-5 w-5" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-medium animate-pulse">
                  {itemCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Barre de recherche mobile/desktop */}
        {searchOpen && (
          <div className="pb-4 animate-fadeIn">
            <form onSubmit={handleSearch} className="relative max-w-2xl mx-auto">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher un bijou..."
                className="w-full pl-12 pr-4 py-3 bg-white/95 backdrop-blur-sm border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 shadow-lg text-gray-900 placeholder-gray-500"
                autoFocus
              />
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            </form>
          </div>
        )}
      </nav>

        {/* Menu mobile */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-50">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)} />
          <nav className="fixed top-0 left-0 bottom-0 flex flex-col w-5/6 max-w-sm py-6 px-6 bg-white overflow-y-auto shadow-2xl">
              <div className="flex items-center justify-between mb-8">
              <div className="flex items-center">
                <img 
                  src="/assets/logo/aupiho-logo.png" 
                  alt="Aupiho" 
                  className="h-12 w-auto object-contain"
                />
              </div>
                <button
                  type="button"
                className="-mr-2 p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="sr-only">Fermer le menu</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>

              {/* Liens de navigation mobile */}
              <div className="space-y-1">
                <Link
                to="/nouveautes-bijoux-aupiho-joaillerie"
                className="block px-4 py-3 rounded-lg text-base font-medium text-gray-700 hover:text-[#497aab] hover:bg-gray-50 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                Nouveautés
                </Link>
                <Link
                  to="/products"
                className="block px-4 py-3 rounded-lg text-base font-medium text-gray-700 hover:text-[#497aab] hover:bg-gray-50 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Nos Bijoux
              </Link>
                <Link
                  to="/"
                className="block px-4 py-3 rounded-lg text-base font-medium text-gray-700 hover:text-[#497aab] hover:bg-gray-50 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                Maison Aupiho
                </Link>
              <Link
                to="/bijoux-sur-mesure"
                className="block px-4 py-3 rounded-lg text-base font-medium text-gray-700 hover:text-[#497aab] hover:bg-gray-50 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Bijoux sur mesure
              </Link>
              <Link
                to="/"
                className="block px-4 py-3 rounded-lg text-base font-medium text-gray-700 hover:text-[#497aab] hover:bg-gray-50 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Points de vente
              </Link>
              <Link
                to="/contactez-nous"
                className="block px-4 py-3 rounded-lg text-base font-medium text-gray-700 hover:text-[#497aab] hover:bg-gray-50 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                Contact
                </Link>
                <Link
                  to="/categories"
                className="block px-4 py-3 rounded-lg text-base font-medium text-gray-700 hover:text-[#497aab] hover:bg-gray-50 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Catégories
                </Link>
                <Link
                  to="/sale"
                className="block px-4 py-3 rounded-lg text-base font-medium text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Promotions
                </Link>
              </div>

            {/* Info contact mobile */}
            <div className="mt-auto pt-6 border-t border-gray-200">
              <a href="tel:+33677267276" className="flex items-center gap-2 text-gray-600 hover:text-[#497aab] transition-colors">
                <PhoneIcon className="h-5 w-5" />
                <span className="font-medium">+33 6 77 26 72 76</span>
              </a>
              </div>
            </nav>
          </div>
        )}
    </header>
    </>
  );
};

